/**
 * /app/renata/proveedores — Supplier AI / Comprador Inteligente dashboard.
 */
import { RenataFeatureGate } from "@/components/renata/RenataFeatureGate";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { RenataPageChrome } from "@/components/renata/RenataPageChrome";
import { useWorkspaceTenantId } from "@/hooks/use-workspace-tenant-id";
import { clientRequestHeaders } from "@/lib/sm-api";
import { SUPPLIER_LEXICON, supplierEmptyState } from "@/lib/renata/supplier/experience";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  FileStack,
  GitCompare,
  Lightbulb,
  MessageCircle,
  ShoppingCart,
  Sparkles,
} from "lucide-react";
import { RenataRichContent, parseRichBlocks } from "@/components/renata/RenataRichContent";
import type { RenataProposal } from "@/lib/renata/types";

export const Route = createFileRoute("/app/renata/proveedores")({
  component: SupplierAiDashboardPage,
  head: () => ({ meta: [{ title: "Compras con Renata — Proveedores" }] }),
});

interface Dash {
  conversations: number;
  lists: number;
  ocrDocs: number;
  changes: number;
  draftPos: number;
  compares: number;
  insights: number;
}

function SupplierAiDashboardPage() {
  const tenantId = useWorkspaceTenantId();
  const [data, setData] = useState<Dash | null>(null);
  const [ask, setAsk] = useState("¿Qué oportunidades de compra encontraste?");
  const [busy, setBusy] = useState(false);
  const [reply, setReply] = useState<{
    text: string;
    blocks: ReturnType<typeof parseRichBlocks>["blocks"];
    proposals: RenataProposal[];
  } | null>(null);

  useEffect(() => {
    if (!tenantId) return;
    void (async () => {
      const res = await fetch("/api/renata/proveedores", {
        credentials: "include",
        headers: clientRequestHeaders(tenantId),
      });
      if (res.ok) setData((await res.json()) as Dash);
    })();
  }, [tenantId]);

  async function runSmartBuyer() {
    if (!tenantId || busy) return;
    setBusy(true);
    try {
      const res = await fetch("/api/renata/proveedores", {
        method: "POST",
        credentials: "include",
        headers: {
          ...clientRequestHeaders(tenantId),
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ action: "smart-buyer", message: ask }),
      });
      if (!res.ok) throw new Error("fail");
      const json = (await res.json()) as {
        content: string;
        proposals: RenataProposal[];
      };
      const parsed = parseRichBlocks(json.content);
      setReply({ text: parsed.text, blocks: parsed.blocks, proposals: json.proposals ?? [] });
    } catch {
      setReply({
        text: "No pude armar la comparativa ahora.",
        blocks: [],
        proposals: [],
      });
    } finally {
      setBusy(false);
    }
  }

  const empty = supplierEmptyState();

  return (
    <RenataFeatureGate featureId="smart_buyer">
    <RenataPageChrome
      screenId="supplier-ai"
      title={SUPPLIER_LEXICON.smartBuyer}
      subtitle="Compará proveedores, listas y reposición — siempre con tablas y aprobación"
    >
      <div className="space-y-6 p-4">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <p className="text-sm text-muted-foreground max-w-xl">
            Renata no es solo un chat: responde con KPIs, tablas, diferencias y propuestas
            listas para aprobar. Nada se aplica solo.
          </p>
          <div className="flex gap-2">
            <Button asChild size="sm" variant="outline">
              <Link to="/app/operaciones/proveedores">Proveedores</Link>
            </Button>
            <Button asChild size="sm" variant="outline">
              <Link to="/app/compras/ordenes" search={{} as never}>Órdenes de compra</Link>
            </Button>
            <Button asChild size="sm" variant="secondary">
              <Link to="/app/renata/chat" search={{} as never}>Abrir chat</Link>
            </Button>
          </div>
        </div>

        {data && (
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <Kpi icon={MessageCircle} label="Conversaciones" value={data.conversations} />
            <Kpi icon={FileStack} label="Listas" value={data.lists} />
            <Kpi icon={Sparkles} label="Documentos leídos" value={data.ocrDocs} />
            <Kpi icon={GitCompare} label="Cambios" value={data.changes} />
            <Kpi icon={ShoppingCart} label="OC propuestas" value={data.draftPos} />
            <Kpi icon={GitCompare} label="Comparativas" value={data.compares} />
            <Kpi icon={Lightbulb} label="Oportunidades" value={data.insights} />
          </div>
        )}

        {!data && (
          <div className="rounded-xl border border-dashed p-6 text-center">
            <p className="font-medium">{empty.title}</p>
            <p className="mt-1 text-sm text-muted-foreground">{empty.body}</p>
          </div>
        )}

        <section className="rounded-xl border p-4 space-y-3">
          <h2 className="text-sm font-medium flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-violet-500" />
            Preguntale al Comprador Inteligente
          </h2>
          <Textarea
            value={ask}
            onChange={(e) => setAsk(e.target.value)}
            rows={2}
            className="text-sm"
            placeholder="Ej: ¿Quién aumentó más los precios este mes?"
          />
          <div className="flex flex-wrap gap-2">
            {[
              "¿Qué proveedor me conviene para este producto?",
              "¿Quién aumentó más los precios este mes?",
              "¿Qué productos debería comprar hoy?",
              "¿Qué oportunidades de compra encontraste?",
            ].map((q) => (
              <button
                key={q}
                type="button"
                className="rounded-full border px-2.5 py-1 text-[11px] text-muted-foreground hover:bg-muted"
                onClick={() => setAsk(q)}
              >
                {q}
              </button>
            ))}
          </div>
          <Button onClick={() => void runSmartBuyer()} disabled={busy}>
            {busy ? "Analizando…" : "Analizar con Renata"}
          </Button>

          {reply && (
            <div className="mt-4 space-y-3 rounded-lg border bg-muted/20 p-3">
              <p className="text-sm whitespace-pre-wrap">{reply.text}</p>
              {reply.blocks.map((b, i) => (
                <RenataRichContent key={i} block={b} />
              ))}
              {reply.proposals.length > 0 && (
                <div className="space-y-2">
                  <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                    Propuestas para aprobación
                  </p>
                  {reply.proposals.map((p) => (
                    <div key={p.id} className="rounded-lg border bg-background p-3 text-sm">
                      <p className="font-medium">{p.label}</p>
                      <p className="text-xs text-muted-foreground">{p.description}</p>
                      {p.preview && (
                        <pre className="mt-2 whitespace-pre-wrap rounded bg-muted p-2 text-[11px]">
                          {p.preview}
                        </pre>
                      )}
                    </div>
                  ))}
                  <Button asChild size="sm" variant="secondary">
                    <Link to="/app/renata/chat" search={{} as never}>Continuar en chat para aprobar</Link>
                  </Button>
                </div>
              )}
            </div>
          )}
        </section>
      </div>
    </RenataPageChrome>
    </RenataFeatureGate>
  );
}

function Kpi({
  label,
  value,
  icon: Icon,
}: {
  label: string;
  value: number;
  icon: React.ComponentType<{ className?: string }>;
}) {
  return (
    <div className="rounded-xl border bg-card p-3">
      <div className="flex items-center gap-2 text-xs text-muted-foreground">
        <Icon className="h-3.5 w-3.5" />
        {label}
      </div>
      <p className="mt-1 text-2xl font-semibold tabular-nums">{value}</p>
    </div>
  );
}
